import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { DashboardLayout } from "./layouts/DashboardLayout";
import LoginPage from "./pages/LoginPage";
import LandingPage from "./pages/LandingPage";
import ApproveUserPage from "./pages/ApproveUserPage";
import DashboardPage from "./pages/DashboardPage";
import InicioPage from "./pages/InicioPage";
import PortfolioPage from "./pages/PortfolioPage";
import AwardsPage from "./pages/AwardsPage";
import TendersPage from "./pages/TendersPage";
import InitiativeDetailPage from "./pages/InitiativeDetailPage";
import PilotsPage from "./pages/PilotsPage";
import ProductionPage from "./pages/ProductionPage";
import ProjectsPage from "./pages/ProjectsPage";
import GrantsPage from "./pages/GrantsPage";
import ProgramasPage from "./pages/ProgramasPage";
import PartnersPage from "./pages/PartnersPage";
import ImpactoPage from "./pages/ImpactoPage";
import FinanzasPage from "./pages/FinanzasPage";
import ComunicacionPage from "./pages/ComunicacionPage";
import EquipoPage from "./pages/EquipoPage";
import PlanificacionPage from "./pages/PlanificacionPage";
import DocsPage from "./pages/DocsPage";
import SettingsPage from "./pages/SettingsPage";
import AdminPage from "./pages/AdminPage";
import CrossLinkAdminPage from "./pages/CrossLinkAdminPage";
import VigilanciaPage from "./pages/VigilanciaPage";
import ProfilePage from "./pages/ProfilePage";
import PatentsPage from "./pages/PatentsPage";
import ObservatorioShell from "./pages/ObservatorioShell";
import EventosPage from "./pages/EventosPage";
import ShakersAdminPage from "./pages/equipo/ShakersAdminPage";
function ShakersRoute() { return <ShakersAdminPage />; }
import ShakersEvaluatePage from "./pages/ShakersEvaluatePage";
import ShakersEvaluateWowPage from "./pages/ShakersEvaluateWowPage";
import ShakersThanksPage from "./pages/ShakersThanksPage";
import ApprovalsPage from "./pages/ApprovalsPage";
import ProposalsPage from "./pages/ProposalsPage";
import PersonalPage from "./pages/PersonalPage";
import ObservatorioEmbedPage from "./pages/ObservatorioEmbedPage";
import TasksBoardPage from "./pages/TasksBoardPage";
import MyTasksPage from "./pages/MyTasksPage";
import SubvencionesEmbedPage from "./pages/SubvencionesEmbedPage";
import EventosCalendarPublicPage from "./pages/EventosCalendarPublicPage";
import EventClaimPage from "./pages/EventClaimPage";
import LaunchPage from "./pages/LaunchPage";
import NovaPage from "./pages/NovaPage";
import NovaCampaignPage from "./pages/NovaCampaignPage";
import NovaDashboardPage from "./pages/NovaDashboardPage";
import NovaConsolidatedPage from "./pages/NovaConsolidatedPage";
import DealflowTimelinePage from "./pages/DealflowTimelinePage";
import DealflowDealsPage from "./pages/DealflowDealsPage";
import ContactsPage from "./pages/ContactsPage";
import InteractionsPage from "./pages/InteractionsPage";
import ClimateTechAcceleratorsPage from "./pages/ClimateTechAcceleratorsPage";
import DuplicateReviewPage from "./pages/DuplicateReviewPage";
import NovaPublicPage from "./pages/NovaPublicPage";
import NovaHallOfFamePage from "./pages/NovaHallOfFamePage";
import AppCatalogPage from "./pages/AppCatalogPage";
import TalentOnboardingPage from "./pages/onboarding/TalentOnboardingPage";
import XVPage from "./pages/XVPage";
import DeepResearchPage from "./pages/DeepResearchPage";
// EcosystemPage: usado indirectamente por ObservatorioShell (lazy import)
import SurveysPage from "./pages/SurveysPage";
import SurveyPublicPage from "./pages/SurveyPublicPage";
import PG07Page from "./pages/PG07Page";
import PG07ExtraFichasPage from "./pages/PG07ExtraFichasPage";
import CierreDashboardPage from "./pages/CierreDashboardPage";
import AuditoriaGlobalPage from "./pages/AuditoriaGlobalPage";
import AurenPage from "./pages/AurenPage";
import FocusDayPage from "./pages/FocusDayPage";
import FirefliesSuggestionsPage from "./pages/FirefliesSuggestionsPage";
import "./App.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/bienvenida" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/launch" element={<LaunchPage />} />
              <Route path="/approve-user" element={<ApproveUserPage />} />
              <Route path="/admin/approve" element={<ApproveUserPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<InicioPage />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="portfolio" element={<PortfolioPage />} />
                <Route path="premios" element={<AwardsPage />} />
                <Route path="licitaciones" element={<TendersPage />} />
                <Route path="initiative/:id" element={<InitiativeDetailPage />} />
                <Route path="tareas" element={<TasksBoardPage />} />
                <Route path="tareas/mias" element={<MyTasksPage />} />
                <Route path="pilots" element={<PilotsPage />} />
                <Route path="production" element={<ProductionPage />} />
                <Route path="projects" element={<ProjectsPage />} />
                <Route path="grants" element={<GrantsPage />} />
                <Route path="programas" element={<ProgramasPage />} />
                <Route path="partners" element={<PartnersPage />} />
                <Route path="impacto" element={<ImpactoPage />} />
                <Route path="finanzas" element={<FinanzasPage />} />
                <Route path="comunicacion" element={<ComunicacionPage />} />
                <Route path="equipo" element={<EquipoPage />} />
                <Route path="planificacion" element={<PlanificacionPage />} />
                <Route path="pg07" element={<PG07Page />} />
                <Route path="pg07/extras" element={<PG07ExtraFichasPage />} />
                <Route path="pg07/cierre-dashboard" element={<CierreDashboardPage />} />
                <Route path="pg07/auditoria" element={<AuditoriaGlobalPage />} />
                <Route path="auren" element={<AurenPage />} />
                <Route path="focus-day" element={<FocusDayPage />} />
                <Route path="fireflies/suggestions" element={<FirefliesSuggestionsPage />} />
                <Route path="docs" element={<DocsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="app-catalog" element={<AppCatalogPage />} />
                <Route path="xv" element={<XVPage />} />
                <Route path="research" element={<DeepResearchPage />} />
                <Route path="ecosystem" element={<Navigate to="/observatorio?tab=ecosistema" replace />} />
                <Route path="surveys" element={<SurveysPage />} />
                <Route path="admin" element={<AdminPage />} />
                <Route path="admin/cross-link" element={<CrossLinkAdminPage />} />
                <Route path="vigilancia" element={<VigilanciaPage />} />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="patents" element={<PatentsPage />} />
                <Route path="observatorio" element={<ObservatorioShell />} />
                <Route path="eventos" element={<EventosPage />} />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="propuestas" element={<ProposalsPage />} />
                <Route path="personal" element={<PersonalPage />} />
                <Route path="nova" element={<NovaPage />} />
                <Route path="nova/dashboard" element={<NovaDashboardPage />} />
                <Route path="nova/consolidado" element={<NovaConsolidatedPage />} />
                <Route path="dealflow/timeline" element={<DealflowTimelinePage />} />
                <Route path="dealflow-deals" element={<DealflowDealsPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="interactions" element={<InteractionsPage />} />
                <Route path="climatetech-accelerators" element={<ClimateTechAcceleratorsPage />} />
                <Route path="admin/duplicados" element={<DuplicateReviewPage />} />
                <Route path="dealflow" element={<Navigate to="/observatorio?tab=dealflow-pipeline" replace />} />
                <Route path="nova/manual" element={<Navigate to="/docs?tab=nova" replace />} />
                <Route path="nova/:id" element={<NovaCampaignPage />} />
                <Route path="shakers/evaluate/:year" element={<ShakersEvaluatePage />} />

                {/* Legacy redirects → hubs consolidados (compat con bookmarks antiguos) */}
                <Route path="finance" element={<Navigate to="/finanzas?tab=analisis" replace />} />
                <Route path="budget" element={<Navigate to="/finanzas?tab=plan" replace />} />
                <Route path="presupuesto-real" element={<Navigate to="/finanzas?tab=real" replace />} />
                <Route path="kpis" element={<Navigate to="/impacto" replace />} />
                <Route path="alerts" element={<Navigate to="/comunicacion?tab=alertas" replace />} />
                <Route path="reports" element={<Navigate to="/comunicacion?tab=reporting" replace />} />
                <Route path="ai-reports" element={<Navigate to="/comunicacion?tab=ia" replace />} />
                <Route path="weeklys" element={<Navigate to="/comunicacion?tab=weeklys" replace />} />
                <Route path="calendar" element={<Navigate to="/planificacion?tab=calendario" replace />} />
                <Route path="gantt" element={<Navigate to="/planificacion?tab=gantt" replace />} />
                <Route path="heatmap" element={<Navigate to="/planificacion?tab=heatmap" replace />} />
                <Route path="gamification" element={<Navigate to="/equipo?tab=gamificacion" replace />} />
                <Route path="bienestar" element={<Navigate to="/equipo?tab=bienestar" replace />} />
                <Route path="vacaciones" element={<Navigate to="/equipo?tab=mytime" replace />} />
                <Route path="timesheets" element={<Navigate to="/equipo?tab=timesheets" replace />} />
                <Route path="talent" element={<Navigate to="/equipo?tab=talent" replace />} />
                <Route path="shakers" element={<ShakersRoute />} />
                <Route path="documents" element={<Navigate to="/docs?tab=documentos" replace />} />
                <Route path="manual" element={<Navigate to="/docs?tab=manual" replace />} />
                <Route path="monetization" element={<Navigate to="/impacto" replace />} />
                <Route path="esg" element={<Navigate to="/impacto" replace />} />
              </Route>
              <Route path="/shakers/thanks" element={<ShakersThanksPage />} />
              {/* Public evaluation page - no auth required */}
              <Route path="/evaluar/:year" element={<ShakersEvaluateWowPage />} />
              {/* Public embed pages for intranet */}
              <Route path="/embed/observatorio" element={<ObservatorioEmbedPage />} />
              <Route path="/embed/subvenciones" element={<SubvencionesEmbedPage />} />
              <Route path="/embed/eventos-calendario" element={<EventosCalendarPublicPage />} />
              {/* Public "Lo cubro yo" claim link from Teams/email reminders */}
              <Route path="/eventos/claim/:eventoId" element={<EventClaimPage />} />
              {/* Public survey response page */}
              <Route path="/encuesta/:token" element={<SurveyPublicPage />} />
              {/* Public NOVA campaign landing (password-protected) */}
              <Route path="/nova/c/:token" element={<NovaPublicPage />} />
              {/* Public NOVA Hall of Fame for office TV (password-protected) */}
              <Route path="/nova/hall-of-fame" element={<NovaHallOfFamePage />} />
              {/* Public Talent onboarding for new hires (token + code protected) */}
              <Route path="/onboarding/talent/:token" element={<TalentOnboardingPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
